import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";
import { apiService } from "@/services/api";
import { toast } from "sonner";
import { SelectField } from "@/components/ui/select";
import {
  BarChart3,
  Users,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Activity,
  Target,
  Award,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

interface GraphData {
  month: string;
  parent: number;
  childCount: number;
}

interface StatisticsData {
  totalUsers: number;
  activeUsers: number;
  newUsersThisMonth: number;
  growthRate: number;
  averageEngagement: number;
  topPerformingMonth: string;
}

export function Statistics() {
  const { currentTheme } = useTheme();
  const [graphData, setGraphData] = useState<GraphData[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState<StatisticsData>({
    totalUsers: 0,
    activeUsers: 0,
    newUsersThisMonth: 0,
    growthRate: 0,
    averageEngagement: 0,
    topPerformingMonth: "",
  });

  // Load graph data
  const loadGraphData = async (year: string) => {
    setLoading(true);
    try {
      const response = await apiService.getGraphData(year);
      console.log("Statistics API response:", response);
      if (response.type === 1) {
        console.log("Setting graph data:", response.data);
        setGraphData(response.data);
        // Calculate statistics from graph data
        calculateStatistics(response.data);
      } else {
        toast.error(response.message || "Failed to load statistics");
      }
    } catch (error) {
      toast.error("Failed to load statistics");
      console.error("Error loading statistics:", error);
      // Set mock data for demonstration
      setGraphData(mockGraphData);
      calculateStatistics(mockGraphData);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics from graph data
  const calculateStatistics = (data: GraphData[]) => {
    console.log("Calculating statistics from data:", data);
    const totalUsers = data.reduce((sum, item) => sum + item.parent, 0);
    const activeUsers = data.reduce((sum, item) => sum + item.childCount, 0);
    const currentMonth = new Date().getMonth();
    const newUsersThisMonth = data[currentMonth]?.parent || 0;

    console.log("Calculated stats:", { totalUsers, activeUsers, newUsersThisMonth, currentMonth });

    // Calculate growth rate (comparing current month to previous month)
    const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const currentMonthUsers = data[currentMonth]?.parent || 0;
    const previousMonthUsers = data[previousMonth]?.parent || 0;
    const growthRate =
      previousMonthUsers > 0
        ? ((currentMonthUsers - previousMonthUsers) / previousMonthUsers) * 100
        : 0;

    // Find top performing month
    const topMonth = data.reduce(
      (max, item) => (item.parent > max.parent ? item : max),
      data[0] || { month: "", parent: 0 }
    );

    setStatistics({
      totalUsers,
      activeUsers,
      newUsersThisMonth,
      growthRate,
      averageEngagement: activeUsers > 0 ? (activeUsers / totalUsers) * 100 : 0,
      topPerformingMonth: topMonth.month,
    });
  };

  useEffect(() => {
    void loadGraphData(selectedYear);
  }, [selectedYear]);

  // Mock data for demonstration
  const mockGraphData: GraphData[] = [
    { month: "Jan", parent: 120, childCount: 85 },
    { month: "Feb", parent: 150, childCount: 110 },
    { month: "Mar", parent: 180, childCount: 140 },
    { month: "Apr", parent: 220, childCount: 170 },
    { month: "May", parent: 280, childCount: 220 },
    { month: "Jun", parent: 320, childCount: 260 },
    { month: "Jul", parent: 380, childCount: 310 },
    { month: "Aug", parent: 420, childCount: 350 },
    { month: "Sep", parent: 450, childCount: 380 },
    { month: "Oct", parent: 480, childCount: 410 },
    { month: "Nov", parent: 520, childCount: 450 },
    { month: "Dec", parent: 580, childCount: 520 },
  ];

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatPercentage = (num: number) => {
    return `${num.toFixed(1)}%`;
  };

  const getMonthName = (month: string) => {
    const months: Record<string, string> = {
      Jan: "January",
      Feb: "February",
      Mar: "March",
      Apr: "April",
      May: "May",
      Jun: "June",
      Jul: "July",
      Aug: "August",
      Sep: "September",
      Oct: "October",
      Nov: "November",
      Dec: "December",
    };
    return months[month] || month;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Statistics & Analytics</h1>
          <p className="text-muted-foreground">View detailed analytics and performance metrics</p>
        </div>
        <div className="flex items-center space-x-2">
          <SelectField
            value={selectedYear}
            onValueChange={setSelectedYear}
            options={Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(
              year => ({
                value: year.toString(),
                label: year.toString(),
              })
            )}
            placeholder="Select year"
            className="w-32"
          />
          <Button
            onClick={() => {
              void loadGraphData(selectedYear);
            }}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(statistics.totalUsers)}</div>
            <p className="text-xs text-muted-foreground">
              +{formatNumber(statistics.newUsersThisMonth)} this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(statistics.activeUsers)}</div>
            <p className="text-xs text-muted-foreground">
              {formatPercentage(statistics.averageEngagement)} engagement rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Growth Rate</CardTitle>
            {statistics.growthRate >= 0 ? (
              <TrendingUp className="h-4 w-4 text-green-500" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-500" />
            )}
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                statistics.growthRate >= 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              {formatPercentage(statistics.growthRate)}
            </div>
            <p className="text-xs text-muted-foreground">vs last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Month</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{getMonthName(statistics.topPerformingMonth)}</div>
            <p className="text-xs text-muted-foreground">Best performance</p>
          </CardContent>
        </Card>
      </div>

      {/* Graph Data */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly User Growth</CardTitle>
          <CardDescription>User registration trends for {selectedYear}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              Loading statistics...
            </div>
          ) : (
            <div className="space-y-4">
              {/* Bar Chart Visualization */}
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={graphData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} domain={[0, "dataMax + 1"]} />
                    <Tooltip
                      formatter={(value, name) => [
                        typeof value === "number" ? value.toLocaleString() : value,
                        name === "parent" ? "Total Users" : "Staff Users",
                      ]}
                      labelFormatter={label => `Month: ${label}`}
                      contentStyle={{
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                      }}
                    />
                    <Bar
                      dataKey="parent"
                      fill={currentTheme.colors.primary + "20"}
                      name="Total Users"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="childCount"
                      fill={currentTheme.colors.primary}
                      name="Staff Users"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Line Chart for Growth Trends */}
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={graphData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} domain={[0, "dataMax + 1"]} />
                    <Tooltip
                      formatter={(value, name) => [
                        typeof value === "number" ? value.toLocaleString() : value,
                        name === "parent" ? "Total Users" : "Staff Users",
                      ]}
                      labelFormatter={label => `Month: ${label}`}
                      contentStyle={{
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="parent"
                      stroke={currentTheme.colors.primary + "80"}
                      strokeWidth={3}
                      name="Total Users"
                      dot={{ fill: currentTheme.colors.primary + "80", strokeWidth: 2, r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="childCount"
                      stroke={currentTheme.colors.primary}
                      strokeWidth={3}
                      name="Staff Users"
                      dot={{ fill: currentTheme.colors.primary, strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Data Table */}
              <div className="mt-6">
                <h3 className="text-lg font-medium mb-4">Detailed Data</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Month</th>
                        <th className="text-right py-2">Total Users</th>
                        <th className="text-right py-2">Staff Users</th>
                        <th className="text-right py-2">Growth</th>
                      </tr>
                    </thead>
                    <tbody>
                      {graphData.map((item, index) => {
                        const previousMonth = index > 0 ? graphData[index - 1].parent : 0;
                        const growth =
                          previousMonth > 0
                            ? ((item.parent - previousMonth) / previousMonth) * 100
                            : 0;

                        return (
                          <tr key={index} className="border-b">
                            <td className="py-2 font-medium">{getMonthName(item.month)}</td>
                            <td className="text-right py-2">{formatNumber(item.parent)}</td>
                            <td className="text-right py-2">{formatNumber(item.childCount)}</td>
                            <td
                              className={`text-right py-2 ${
                                growth >= 0 ? "text-green-600" : "text-red-600"
                              }`}
                            >
                              {growth >= 0 ? "+" : ""}
                              {formatPercentage(growth)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Additional Analytics */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Performance Insights</CardTitle>
            <CardDescription>Key performance indicators</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm">Average Monthly Growth</span>
              <span className="font-medium text-green-600">
                {formatPercentage(statistics.growthRate)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">User Retention Rate</span>
              <span className="font-medium">{formatPercentage(statistics.averageEngagement)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Peak Registration Month</span>
              <span className="font-medium">{getMonthName(statistics.topPerformingMonth)}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common administrative tasks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button variant="outline" className="w-full justify-start">
              <Users className="w-4 h-4 mr-2" />
              View All Users
            </Button>
            <Button variant="outline" className="w-full justify-start">
              <BarChart3 className="w-4 h-4 mr-2" />
              Export Report
            </Button>
            <Button variant="outline" className="w-full justify-start">
              <Target className="w-4 h-4 mr-2" />
              Set Targets
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
